import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle, X, Minus, Search, Send, Paperclip, Check, CheckCheck,
  Users, User, Building2, ChevronLeft, Settings, Bell, BellOff, MoreVertical, LogOut, RefreshCw, Shield,
  Image, FileText, Download, Loader, Circle, ChevronDown, Trash2, AlertTriangle, Calendar, Globe, MessageSquare
} from 'lucide-react';
import apiService from '../services/api';
import signalRService from '../services/signalr';
import PatientPanel from './PatientPanel';

// Backend base URL for file downloads - uses config.js or current origin
const BACKEND_BASE = window.CHATBOT_CONFIG?.API_URL
  ? window.CHATBOT_CONFIG.API_URL.replace(/\/api$/, '')
  : `http://${window.location.hostname}:5500`;

const getFileUrl = (url) => {
  if (!url) return null;

  // Absolute URL - extract path and rebuild with current backend
  if (url.startsWith('http')) {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname + urlObj.search;
      return `${BACKEND_BASE}${path}`;
    } catch {
      return url;
    }
  }

  // Relative URL - prepend backend base
  return `${BACKEND_BASE}${url}`;
};

const ChatPanel = ({ isEmbedded, onClose, onMinimize, currentUser, onLogout, onOpenAdmin, onUnreadCountChange }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('people');
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [myStatus, setMyStatus] = useState(currentUser?.status || 0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOption, setDeleteOption] = useState('all'); // 'all' or 'range'
  const [deleteDateFrom, setDeleteDateFrom] = useState('');
  const [deleteDateTo, setDeleteDateTo] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [typingUser, setTypingUser] = useState(null); // { userId, userName }
  const [notificationEnabled, setNotificationEnabled] = useState(() => {
    const saved = localStorage.getItem('chatbot_notification_enabled');
    return saved !== 'false'; // default true
  });
  const [showDeptMembersModal, setShowDeptMembersModal] = useState(false);
  const [deptMembers, setDeptMembers] = useState([]);
  const [selectedDeptName, setSelectedDeptName] = useState('');
  const [loadingDeptMembers, setLoadingDeptMembers] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [myAvatar, setMyAvatar] = useState(currentUser?.avatar || null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'tr');
  const [appSettings, setAppSettings] = useState({});
  const [showPatientPanel, setShowPatientPanel] = useState(false);
  const [patientWaitingCount, setPatientWaitingCount] = useState(0);
  const [patientToast, setPatientToast] = useState(null);
  const patientToastTimerRef = useRef(null);
  const avatarInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Initialize notification sound
  useEffect(() => {
    // Create audio element with a soft notification sound (base64 encoded)
    const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZUPOZqAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAARMQWME//Lpf5c//+X5d/l//////0uk//wy/y7/y7/l0v/+WQwYDBhghh/l3+Xf5dL/y7/hgwY2NjBhYWFhYWFhYf/y6X/4Y/DH8u/y7////+XS4YMbG5ubm5u7u7u7u7u7/+7u7u7u7u7u7v/u7u7u7u7u7u7u7u7u7u7v/u7u7u7u7u7//+1DEKAPHA15/8AAAIAAANP8AAABu7u7u7u7u7u7u7u7u7u7//7u7u7u7u7u7u7u7u7u7u7/+7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u4=');
    audio.volume = 0.3;
    notificationSoundRef.current = audio;

    // Unlock audio on first user interaction (click anywhere)
    const unlockAudio = () => {
      if (!audioUnlockedRef.current && notificationSoundRef.current) {
        notificationSoundRef.current.play().then(() => {
          notificationSoundRef.current.pause();
          notificationSoundRef.current.currentTime = 0;
          audioUnlockedRef.current = true;
          console.log('Audio unlocked');
        }).catch(() => {});
      }
    };

    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Load app settings and apply default language
  useEffect(() => {
    const loadAppSettings = async () => {
      try {
        const data = await apiService.request('/chat/app-settings');
        setAppSettings(data || {});

        // Language priority: localStorage (user choice) > appSettings > 'tr'
        const userLang = localStorage.getItem('chatbot_language');
        const defaultLang = data?.defaultLanguage;
        const lang = userLang || defaultLang || 'tr';
        if (lang !== i18n.language) {
          i18n.changeLanguage(lang);
          setCurrentLanguage(lang);
        }
      } catch (err) {
        console.error('App settings yüklenemedi:', err);
        // Fallback: use localStorage or 'tr'
        const lang = localStorage.getItem('chatbot_language') || 'tr';
        if (lang !== i18n.language) {
          i18n.changeLanguage(lang);
          setCurrentLanguage(lang);
        }
      }
    };
    loadAppSettings();
  }, [i18n]);

  // Load patient waiting count
  useEffect(() => {
    const loadPatientCount = async () => {
      try {
        const data = await apiService.request('/patient/queue/summary');
        setPatientWaitingCount(data?.waitingCount || 0);
      } catch {}
    };
    loadPatientCount();
    const interval = setInterval(loadPatientCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!notificationEnabled) return;
    if (notificationSoundRef.current) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(() => {});
    }
  }, [notificationEnabled]);

  const toggleNotification = () => {
    const newValue = !notificationEnabled;
    setNotificationEnabled(newValue);
    localStorage.setItem('chatbot_notification_enabled', String(newValue));
  };

  const showDeptMembers = async (dept, e) => {
    e.stopPropagation();
    setSelectedDeptName(dept.name);
    setLoadingDeptMembers(true);
    setShowDeptMembersModal(true);
    try {
      const members = await apiService.request(`/chat/departments/${dept.id}/members`);
      setDeptMembers(members);
    } catch (err) {
      console.error('Failed to load members:', err);
      setDeptMembers([]);
    } finally {
      setLoadingDeptMembers(false);
    }
  };

  const [statusDefinitions, setStatusDefinitions] = useState([]);

  const statusValueToI18nKey = {
    0: 'online',
    1: 'away',
    2: 'busy',
    3: 'inMeeting',
    4: 'inProcedure',
    5: 'onLeave',
    6: 'offline'
  };

  const defaultStatusOptions = useMemo(() => [
    { value: 0, label: t('status.online'), color: '#22c55e' },
    { value: 1, label: t('status.away'), color: '#f59e0b' },
    { value: 2, label: t('status.busy'), color: '#ef4444' },
    { value: 3, label: t('status.inMeeting'), color: '#8b5cf6' },
    { value: 4, label: t('status.inProcedure'), color: '#3b82f6' },
    { value: 5, label: t('status.onLeave'), color: '#6b7280' },
    { value: 6, label: t('status.offline'), color: '#9ca3af' }
  ], [t]);

  const statusOptions = useMemo(() => {
    if (Array.isArray(statusDefinitions) && statusDefinitions.length > 0) {
      return statusDefinitions.map(def => {
        const localKey = statusValueToI18nKey[def.value];
        const translatedLabel = localKey ? t(`status.${localKey}`) : undefined;

        return {
          value: def.value,
          label: (def.label || translatedLabel || '').trim(),
          color: def.color || '#9ca3af'
        };
      });
    }

    return defaultStatusOptions;
  }, [statusDefinitions, defaultStatusOptions, t]);

  const languageOptions = useMemo(() => [
    { code: 'tr', label: t('language.turkish'), flag: '🇹🇷' },
    { code: 'en', label: t('language.english'), flag: '🇺🇸' }
  ], [t]);

  const loadStatusDefinitions = async (lang = 'tr') => {
    try {
      const defs = await apiService.request(`/chat/statuses?lang=${lang}`);
      setStatusDefinitions(Array.isArray(defs) ? defs : []);
    } catch (err) {
      console.error('Durum tanımları yüklenemedi:', err);
    }
  };

  useEffect(() => {
    loadStatusDefinitions(currentLanguage);
  }, [currentLanguage]);

  const handleStatusChange = async (status) => {
    try {
      const statusText = getStatusLabel(status);
      await apiService.updateStatus(status, statusText);
      setMyStatus(status);
      setShowStatusMenu(false);
      setUsers(prevUsers => prevUsers.map(u => ({
        ...u,
        statusText: getStatusLabel(u.status ?? 6)
      })));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setCurrentLanguage(languageCode);
    localStorage.setItem('chatbot_language', languageCode);
    setShowLanguageMenu(false);
    loadStatusDefinitions(languageCode);
  };

  useEffect(() => {
    setUsers(prevUsers => prevUsers.map(u => ({
      ...u,
      statusText: getStatusLabel(u.status ?? 6)
    })));
  }, [statusOptions]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Sadece resim dosyaları yüklenebilir');
      return;
    }

    // Validate file size (max 5MB)
    if (file.length > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const result = await apiService.uploadAvatar(formData);
      if (result.avatarUrl) {
        setMyAvatar(result.avatarUrl);
        // Update currentUser in localStorage
        const storedUser = JSON.parse(localStorage.getItem('chatbot_user') || '{}');
        storedUser.avatar = result.avatarUrl;
        localStorage.setItem('chatbot_user', JSON.stringify(storedUser));
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
      alert('Fotoğraf yüklenemedi: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    if (!myAvatar) return;
    
    if (!confirm('Profil fotoğrafınızı silmek istediğinize emin misiniz?')) return;

    setUploadingAvatar(true);
    try {
      await apiService.deleteAvatar();
      setMyAvatar(null);
      // Update currentUser in localStorage
      const storedUser = JSON.parse(localStorage.getItem('chatbot_user') || '{}');
      storedUser.avatar = null;
      localStorage.setItem('chatbot_user', JSON.stringify(storedUser));
    } catch (err) {
      console.error('Avatar delete failed:', err);
      alert('Fotoğraf silinemedi');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getStatusColor = (status) => {
    return statusOptions.find(s => s.value === status)?.color || '#9ca3af';
  };

  const getStatusLabel = (status) => {
    return statusOptions.find(s => s.value === status)?.label || t('status.offline');
  };

  const handleDeleteMessages = async () => {
    if (!selectedChat) return;
    
    setDeleting(true);
    try {
      const targetUserId = selectedChat.type === 'user' ? selectedChat.data.id : null;
      const targetDepartmentId = selectedChat.type === 'department' ? selectedChat.data.id : null;
      const deleteAll = deleteOption === 'all';
      
      const result = await apiService.deleteMessages(
        targetUserId,
        targetDepartmentId,
        deleteAll ? null : deleteDateFrom || null,
        deleteAll ? null : deleteDateTo || null,
        deleteAll
      );
      
      if (result.success) {
        alert(`${result.deletedCount} mesaj silindi`);
        setMessages([]);
        setShowDeleteModal(false);
        setDeleteOption('all');
        setDeleteDateFrom('');
        setDeleteDateTo('');
      } else {
        alert(result.message || 'Silme başarısız');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Mesajlar silinirken hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  // Load users and departments
  const loadData = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const [usersData, deptsData] = await Promise.all([
        apiService.getUsers(currentUser.id),
        apiService.getDepartments(currentUser.id)
      ]);
      setUsers(usersData.filter(u => u.id !== currentUser.id).map(u => ({
        ...u,
        statusText: u.statusText || getStatusLabel(u.status ?? 6)
      })));
      setDepartments(deptsData);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  }, [currentUser]);

  // Load messages for selected chat
  const loadMessages = useCallback(async () => {
    if (!selectedChat || !currentUser) return;
    setLoading(true);
    try {
      let msgs;
      if (selectedChat.type === 'user') {
        msgs = await apiService.getDirectMessages(selectedChat.data.id, currentUser.id);
        // Mark messages as read and update unread count
        await apiService.markAllAsRead(selectedChat.data.id, null, currentUser.id);
        setUsers(prev => prev.map(u => 
          u.id === selectedChat.data.id ? { ...u, unreadCount: 0 } : u
        ));
      } else {
        msgs = await apiService.getDepartmentMessages(selectedChat.data.id, currentUser.id);
        // Mark department messages as read
        await apiService.markAllAsRead(null, selectedChat.data.id, currentUser.id);
        setDepartments(prev => prev.map(d => 
          d.id === selectedChat.data.id ? { ...d, unreadCount: 0 } : d
        ));
      }
      setMessages(msgs);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedChat, currentUser]);

  const updateTotalUnread = useCallback(() => {
    const total = users.reduce((sum, u) => sum + (u.unreadCount || 0), 0)
      + departments.reduce((sum, d) => sum + (d.unreadCount || 0), 0);
    setUnreadTotal(total);
    if (onUnreadCountChange) onUnreadCountChange(total);
  }, [users, departments, onUnreadCountChange]);

  // Initial data load
  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  useEffect(() => {
    updateTotalUnread();
  }, [updateTotalUnread]);

  // Load messages when chat selected
  useEffect(() => {
    loadMessages();
  }, [selectedChat, loadMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // SignalR connection - run once per user
  const signalRConnectedRef = useRef(false);
  useEffect(() => {
    if (!currentUser || signalRConnectedRef.current) return;

    const token = apiService.getToken();
    if (token) {
      signalRConnectedRef.current = true;
      signalRService.connect(token).then(success => {
        setConnected(success);
        if (success && currentUser.departmentId) {
          signalRService.joinDepartment(currentUser.departmentId);
        }
      });
    }

    return () => {
      signalRConnectedRef.current = false;
      signalRService.disconnect();
    };
  }, [currentUser]);

  // SignalR event handlers - use refs to avoid re-subscribing
  const loadDataRef = useRef(loadData);
  const selectedChatRef = useRef(selectedChat);
  const currentUserRef = useRef(currentUser);
  
  useEffect(() => {
    loadDataRef.current = loadData;
    selectedChatRef.current = selectedChat;
    currentUserRef.current = currentUser;
  });

  useEffect(() => {
    const unsubMessage = signalRService.onMessage((msg) => {
      const currentUserId = currentUserRef.current?.id;
      const chat = selectedChatRef.current;

      // Ignore messages sent by me (I already have them from API response)
      if (msg.senderId === currentUserId) {
        return;
      }

      if (notificationEnabled) {
        playNotificationSound();
      }

      // Check if message is for current chat (user is viewing this conversation)
      const isForCurrentChat = chat && (
        (chat.type === 'user' && msg.senderId === chat.data.id) ||
        (chat.type === 'department' && msg.departmentId === chat.data.id)
      );

      if (isForCurrentChat) {
        // User is viewing this chat - add message and mark as read
        setMessages(prev => {
          // Check for duplicate by ID or by content+sender+time (within 2 seconds)
          if (prev.some(m => m.id === msg.id)) return prev;
          const recentDuplicate = prev.find(m => 
            m.senderId === msg.senderId && 
            m.content === msg.content &&
            Math.abs(new Date(m.sentAt) - new Date(msg.sentAt)) < 2000
          );
          if (recentDuplicate) return prev;
          return [...prev, { ...msg, content: msg.content }];
        });
        
        // Mark as read immediately
        apiService.markAllAsRead(
          chat.type === 'user' ? chat.data.id : null,
          chat.type === 'department' ? chat.data.id : null,
          currentUserId
        ).catch(() => {});
      } else {
        // User is NOT viewing this chat - increment unread and refresh
        const senderUserId = msg.senderId;

        if (senderUserId) {
          setUsers(prev => prev.map(u => u.id === senderUserId ? { ...u, unreadCount: (u.unreadCount || 0) + 1 } : u ));
        }
        if (msg.departmentId) {
          setDepartments(prev => prev.map(d => d.id === msg.departmentId ? { ...d, unreadCount: (d.unreadCount || 0) + 1 } : d ));
        }

        loadDataRef.current();
      }
    });

    const unsubUserOnline = signalRService.onUserOnline((userId) => {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, isOnline: true } : u));
    });

    const unsubUserOffline = signalRService.onUserOffline((userId) => {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, isOnline: false } : u));
    });

    const unsubTyping = signalRService.onTyping((userId, userName) => {
      const chat = selectedChatRef.current;
      // Show typing only if from current chat partner
      if (chat && chat.type === 'user' && chat.data.id === userId) {
        setTypingUser({ userId, userName });
        // Clear typing after 3 seconds
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    });

    // Patient queue notification - listen for new patient messages
    const unsubPatientQueue = signalRService.onPatientQueueUpdated(() => {
      // Refresh patient waiting count
      apiService.request('/patient/queue/summary').then(data => {
        const newCount = data?.waitingCount || 0;
        setPatientWaitingCount(prev => {
          if (newCount > prev) {
            // New patient waiting - show toast and play sound
            playNotificationSound();
            setPatientToast({ type: 'queue', count: newCount });
            if (patientToastTimerRef.current) clearTimeout(patientToastTimerRef.current);
            patientToastTimerRef.current = setTimeout(() => setPatientToast(null), 5000);
          }
          return newCount;
        });
      }).catch(() => {});
    });

    const unsubPatientMessage = signalRService.onPatientMessage((msg) => {
      // A patient sent a message - show toast notification
      if (msg.senderType === 0) { // 0 = Patient
        playNotificationSound();
        setPatientToast({ type: 'message', name: msg.senderName || 'Patient', content: msg.content });
        if (patientToastTimerRef.current) clearTimeout(patientToastTimerRef.current);
        patientToastTimerRef.current = setTimeout(() => setPatientToast(null), 5000);
      }
      // Refresh waiting count
      apiService.request('/patient/queue/summary').then(data => {
        setPatientWaitingCount(data?.waitingCount || 0);
      }).catch(() => {});
    });

    const unsubPatientAssigned = signalRService.onPatientSessionAssigned((data) => {
      if (data.assignedToUserId === currentUserRef.current?.id) {
        playNotificationSound();
        setPatientToast({ type: 'assigned', name: data.patientName });
        if (patientToastTimerRef.current) clearTimeout(patientToastTimerRef.current);
        patientToastTimerRef.current = setTimeout(() => setPatientToast(null), 5000);
      }
    });

    return () => {
      unsubMessage();
      unsubUserOnline();
      unsubUserOffline();
      unsubTyping();
      unsubPatientQueue();
      unsubPatientMessage();
      unsubPatientAssigned();
    };
  }, [playNotificationSound]); // Only re-subscribe when playNotificationSound changes

  const handleSend = async () => {
    if (!message.trim() || !currentUser) return;

    const dto = {
      content: message,
      messageType: 0,
      receiverId: selectedChat.type === 'user' ? selectedChat.data.id : null,
      departmentId: selectedChat.type === 'department' ? selectedChat.data.id : null,
    };

    try {
      const sentMsg = await apiService.sendMessage(dto, currentUser.id);
      setMessages(prev => [...prev, sentMsg]);
      setMessage('');
      // SignalR is handled by backend - no need to send separately
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Send typing indicator (throttled to every 2 seconds)
  const handleTyping = () => {
    if (!selectedChat) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    
    signalRService.sendTyping(
      selectedChat.type === 'user' ? selectedChat.data.id : null,
      selectedChat.type === 'department' ? selectedChat.data.id : null
    );
  };

  // File handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleSendWithFile = async () => {
    if (!currentUser || (!message.trim() && !selectedFile)) return;

    try {
      let attachmentUrl = null;
      let attachmentName = null;
      let messageType = 0; // Text

      // Upload file first if selected
      if (selectedFile) {
        setUploading(true);
        const uploadResult = await apiService.uploadFile(selectedFile);
        attachmentUrl = uploadResult.fileUrl;
        attachmentName = uploadResult.fileName;
        messageType = uploadResult.isImage ? 1 : 2; // 1=Image, 2=File
        setUploading(false);
      }

      const dto = {
        content: message || attachmentName || '',
        messageType,
        receiverId: selectedChat.type === 'user' ? selectedChat.data.id : null,
        departmentId: selectedChat.type === 'department' ? selectedChat.data.id : null,
        attachmentUrl,
        attachmentName,
      };

      const sentMsg = await apiService.sendMessage(dto, currentUser.id);
      setMessages(prev => [...prev, sentMsg]);
      setMessage('');
      clearSelectedFile();

      if (connected) {
        signalRService.sendMessage(dto);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setUploading(false);
    }
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return FileText;
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return Image;
    return FileText;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter(user =>
    user.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Chat List View
  const renderChatList = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Clickable Avatar */}
            <button
              onClick={() => setShowProfileModal(true)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: 0
              }}
              title="Profil"
            >
              {myAvatar ? (
                <img 
                  src={getFileUrl(myAvatar)} 
                  alt="Avatar" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                  {currentUser?.fullName?.charAt(0) || '?'}
                </span>
              )}
            </button>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>
                {appSettings.headerTitle || currentUser?.fullName}
              </div>
              <div
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '11px',
                  opacity: 0.9,
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: getStatusColor(myStatus)
                }} />
                {appSettings.headerSubtitle ? (
                  <span>{appSettings.headerSubtitle} - {getStatusLabel(myStatus)}</span>
                ) : (
                  <span>{currentUser?.fullName !== (appSettings.headerTitle || currentUser?.fullName) ? currentUser?.fullName + ' - ' : ''}{getStatusLabel(myStatus)}</span>
                )}
                <ChevronDown size={10} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
              title={t('language.select')}
            >
              <Globe size={18} />
            </button>
            {onOpenAdmin && currentUser?.isAdmin && (
              <button 
                onClick={onOpenAdmin} 
                style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
                title="Yönetim Paneli"
              >
                <Shield size={18} />
              </button>
            )}
            <button 
              onClick={toggleNotification}
              style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
              title={notificationEnabled ? 'Bildirimleri Kapat' : 'Bildirimleri Aç'}
            >
              {notificationEnabled ? <Bell size={18} /> : <BellOff size={18} />}
            </button>
            {!isEmbedded && (
              <>
                <button 
                  onClick={onMinimize || onClose} 
                  style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
                  title="Simge Durumuna Küçült"
                >
                  <Minus size={18} />
                </button>
                <button 
                  onClick={onClose} 
                  style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px' }}
                  title="Kapat"
                >
                  <X size={18} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status Menu Dropdown */}
        {showStatusMenu && (
          <div style={{
            position: 'absolute',
            top: '50px',
            left: '16px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 100,
            overflow: 'hidden',
            minWidth: '140px'
          }}>
            {statusOptions.map(option => (
              <button
                key={option.value}
                onClick={() => handleStatusChange(option.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  background: myStatus === option.value ? '#eff6ff' : 'white',
                  color: '#1f2937',
                  fontSize: '12px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: option.color
                }} />
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Language Menu Dropdown */}
        {showLanguageMenu && (
          <div style={{
            position: 'absolute',
            top: '50px',
            right: '16px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 100,
            overflow: 'hidden',
            minWidth: '120px'
          }}>
            {languageOptions.map(option => (
              <button
                key={option.code}
                onClick={() => handleLanguageChange(option.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  background: currentLanguage === option.code ? '#eff6ff' : 'white',
                  color: '#1f2937',
                  fontSize: '12px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <span style={{ fontSize: '16px' }}>{option.flag}</span>
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '8px',
          padding: '8px 12px',
          gap: '8px'
        }}>
          <Search size={16} style={{ opacity: 0.7 }} />
          <input
            type="text"
            placeholder={t('chat.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        <button
          onClick={() => { setActiveTab('people'); setShowPatientPanel(false); }}
          style={{
            flex: 1,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            background: activeTab === 'people' ? '#eff6ff' : 'white',
            color: activeTab === 'people' ? '#3b82f6' : '#6b7280',
            borderBottom: activeTab === 'people' ? '2px solid #3b82f6' : '2px solid transparent',
            fontWeight: activeTab === 'people' ? '600' : '400'
          }}
        >
          <User size={16} />
          {t('chat.people')}
        </button>
        <button
          onClick={() => { setActiveTab('departments'); setShowPatientPanel(false); }}
          style={{
            flex: 1,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            background: activeTab === 'departments' ? '#eff6ff' : 'white',
            color: activeTab === 'departments' ? '#3b82f6' : '#6b7280',
            borderBottom: activeTab === 'departments' ? '2px solid #3b82f6' : '2px solid transparent',
            fontWeight: activeTab === 'departments' ? '600' : '400'
          }}
        >
          <Building2 size={16} />
          {t('chat.departments')}
        </button>
        <button
          onClick={() => { setActiveTab('patients'); setShowPatientPanel(true); setSelectedChat(null); }}
          style={{
            flex: 1,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            background: activeTab === 'patients' ? '#fffbeb' : 'white',
            color: activeTab === 'patients' ? '#d97706' : '#6b7280',
            borderBottom: activeTab === 'patients' ? '2px solid #d97706' : '2px solid transparent',
            fontWeight: activeTab === 'patients' ? '600' : '400',
            position: 'relative'
          }}
        >
          <MessageSquare size={16} />
          {t('patient.title').split(' ')[0]}
          {patientWaitingCount > 0 && (
            <span style={{
              minWidth: '16px', height: '16px', borderRadius: '8px', fontSize: '10px', fontWeight: '700',
              background: '#f59e0b', color: 'white',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
            }}>
              {patientWaitingCount}
            </span>
          )}
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 'people' ? (
          filteredUsers.map(user => (
            <div
              key={user.id}
              onClick={() => setSelectedChat({ type: 'user', data: user })}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                gap: '12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                background: 'white',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  overflow: 'hidden'
                }}>
                  {user.avatar ? (
                    <img 
                      src={getFileUrl(user.avatar)} 
                      alt={user.fullName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    getInitials(user.fullName)
                  )}
                </div>
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: getStatusColor(user.status ?? 6),
                    border: '2px solid white'
                  }} 
                  title={getStatusLabel(user.status ?? 6)}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '500', color: '#1f2937' }}>{user.fullName}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{user.department}</span>
                  <>
                    <span>•</span>
                    <span style={{ fontStyle: 'italic' }}>{user.statusText || getStatusLabel(user.status ?? 6)}</span>
                  </>
                </div>
              </div>
              {user.unreadCount > 0 && (
                <div style={{
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {user.unreadCount}
                </div>
              )}
            </div>
          ))
        ) : (
          filteredDepartments.map(dept => (
            <div
              key={dept.id}
              onClick={() => setSelectedChat({ type: 'department', data: dept })}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                gap: '12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                background: 'white',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280'
              }}>
                <Users size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '500', color: '#1f2937' }}>
                  {dept.name}
                </div>
                <button
                  onClick={(e) => showDeptMembers(dept, e)}
                  style={{
                    fontSize: '12px',
                    color: '#3b82f6',
                    background: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  {dept.memberCount} {t('department.members')}
                </button>
              </div>
              {dept.unreadCount > 0 && (
                <div style={{
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {dept.unreadCount}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Chat Conversation View
  const renderChatConversation = () => {
    const chatName = selectedChat.type === 'user' 
      ? selectedChat.data.fullName 
      : selectedChat.data.name;
    
    const isOnline = selectedChat.type === 'user' ? selectedChat.data.isOnline : true;

    return (
      <div 
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          position: 'relative'
        }}
      >
        {/* Drag & Drop Overlay */}
        {isDragOver && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(59, 130, 246, 0.1)',
            border: '3px dashed #3b82f6',
            borderRadius: '8px',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}>
            <div style={{
              background: 'white',
              padding: '20px 40px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              textAlign: 'center'
            }}>
              <Paperclip size={32} color="#3b82f6" style={{ marginBottom: '8px' }} />
              <div style={{ color: '#3b82f6', fontWeight: '600' }}>Dosyayı buraya bırak</div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{
          padding: '12px 16px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => setSelectedChat(null)}
            style={{
              background: '#f3f4f6',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronLeft size={20} color="#6b7280" />
          </button>
          
          <div style={{ position: 'relative' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: selectedChat.type === 'user' ? '50%' : '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              overflow: 'hidden'
            }}>
              {selectedChat.type === 'user' ? (
                selectedChat.data.avatar ? (
                  <img 
                    src={getFileUrl(selectedChat.data.avatar)} 
                    alt={chatName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  getInitials(chatName)
                )
              ) : (
                <Users size={18} />
              )}
            </div>
            {selectedChat.type === 'user' && (
              <div style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: isOnline ? '#22c55e' : '#9ca3af',
                border: '2px solid white'
              }} />
            )}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#1f2937' }}>{chatName}</div>
            <div style={{ fontSize: '12px', color: isOnline ? '#22c55e' : '#6b7280' }}>
              {isOnline ? t('chat.online') : t('chat.offline')}
            </div>
          </div>

          <button 
            onClick={() => setShowDeleteModal(true)}
            style={{ background: '#f3f4f6', borderRadius: '8px', padding: '6px' }}
            title="Yazışmaları Temizle"
          >
            <Trash2 size={18} color="#6b7280" />
          </button>
        </div>

        {/* Delete Messages Modal */}
        {showDeleteModal && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              width: '320px',
              maxWidth: '90%'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <AlertTriangle size={24} color="#ef4444" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{t('chat.deleteConversation')}</h3>
              </div>
              
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                {t('chat.deleteConversationConfirm', { chatName })}
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="deleteOption" 
                    checked={deleteOption === 'all'}
                    onChange={() => setDeleteOption('all')}
                  />
                  <span style={{ fontSize: '14px' }}>{t('chat.deleteAll')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="deleteOption" 
                    checked={deleteOption === 'range'}
                    onChange={() => setDeleteOption('range')}
                  />
                  <span style={{ fontSize: '14px' }}>{t('chat.deleteRange')}</span>
                </label>
              </div>

              {deleteOption === 'range' && (
                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                      {t('chat.startDate')}
                    </label>
                    <input 
                      type="date" 
                      value={deleteDateFrom}
                      onChange={(e) => setDeleteDateFrom(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                      {t('chat.endDate')}
                    </label>
                    <input 
                      type="date" 
                      value={deleteDateTo}
                      onChange={(e) => setDeleteDateTo(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteOption('all');
                    setDeleteDateFrom('');
                    setDeleteDateTo('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('chat.cancel')}
                </button>
                <button
                  onClick={handleDeleteMessages}
                  disabled={deleting || (deleteOption === 'range' && (!deleteDateFrom || !deleteDateTo))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: deleting ? '#9ca3af' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: deleting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deleting ? t('chat.deleting') : t('chat.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '16px',
          background: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {/* Date separator */}
          <div style={{ 
            textAlign: 'center', 
            margin: '8px 0',
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            <span style={{
              background: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
              {t('chat.today')}
            </span>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <RefreshCw size={24} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
              {t('chat.noMessages')}
            </div>
          ) : (
            messages.map(msg => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent: msg.isMe ? 'flex-end' : 'flex-start',
                  gap: '8px',
                  alignItems: 'flex-end'
                }}
              >
                {/* Avatar for other users */}
                {!msg.isMe && (
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '600',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {msg.senderAvatar ? (
                      <img 
                        src={getFileUrl(msg.senderAvatar)} 
                        alt={msg.senderName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      msg.senderName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                    )}
                  </div>
                )}
                <div style={{
                  maxWidth: '70%',
                  padding: '10px 14px',
                  borderRadius: msg.isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.isMe 
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                    : 'white',
                  color: msg.isMe ? 'white' : '#1f2937',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  {!msg.isMe && selectedChat.type === 'department' && (
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#3b82f6', marginBottom: '4px' }}>
                      {msg.senderName}
                    </div>
                  )}
                  {/* Image attachment */}
                  {msg.attachmentUrl && isImageUrl(msg.attachmentUrl) && (
                    <div style={{ marginBottom: '8px' }}>
                      <img 
                        src={getFileUrl(msg.attachmentUrl)} 
                        alt={msg.attachmentName || 'Image'} 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(getFileUrl(msg.attachmentUrl), '_blank')}
                      />
                    </div>
                  )}
                  {/* File attachment (non-image) */}
                  {msg.attachmentUrl && !isImageUrl(msg.attachmentUrl) && (
                    <a 
                      href={getFileUrl(msg.attachmentUrl)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: msg.isMe ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        textDecoration: 'none',
                        color: msg.isMe ? 'white' : '#1f2937'
                      }}
                    >
                      <FileText size={20} />
                      <span style={{ flex: 1, fontSize: '13px' }}>{msg.attachmentName || 'File'}</span>
                      <Download size={16} />
                    </a>
                  )}
                  {msg.content && <div style={{ wordBreak: 'break-word' }}>{msg.content}</div>}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '11px',
                    opacity: 0.7
                  }}>
                    <span>{formatTime(msg.sentAt)}</span>
                    {msg.isMe && (
                      msg.isRead 
                        ? <CheckCheck size={14} /> 
                        : <Check size={14} />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* File Preview */}
        {selectedFile && (
          <div style={{
            padding: '8px 16px',
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {filePreview ? (
              <img 
                src={filePreview} 
                alt="Preview" 
                style={{ 
                  width: '60px', 
                  height: '60px', 
                  objectFit: 'cover', 
                  borderRadius: '8px' 
                }} 
              />
            ) : (
              <div style={{
                width: '60px',
                height: '60px',
                background: '#e5e7eb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FileText size={24} color="#6b7280" />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>
                {selectedFile.name}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                {(selectedFile.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button 
              onClick={clearSelectedFile}
              style={{
                background: '#fee2e2',
                borderRadius: '50%',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={16} color="#dc2626" />
            </button>
          </div>
        )}

        {/* Typing Indicator */}
        {typingUser && (
          <div style={{
            padding: '4px 16px',
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            fontSize: '12px',
            color: '#3b82f6',
            fontStyle: 'italic'
          }}>
            {typingUser.userName} yazıyor...
          </div>
        )}

        {/* Input */}
        <div style={{
          padding: '12px 16px',
          background: 'white',
          borderTop: typingUser ? 'none' : '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              background: selectedFile ? '#dbeafe' : '#f3f4f6',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Paperclip size={18} color={selectedFile ? '#3b82f6' : '#6b7280'} />
          </button>
          
          <input
            type="text"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendWithFile();
              }
            }}
            placeholder={selectedFile ? 'Mesaj ekle (opsiyonel)...' : t('chat.typeMessage')}
            style={{
              flex: 1,
              padding: '10px 14px',
              background: '#f3f4f6',
              borderRadius: '20px',
              border: 'none',
              fontSize: '14px'
            }}
          />
          
          <button
            onClick={handleSendWithFile}
            disabled={(!message.trim() && !selectedFile) || uploading}
            style={{
              background: (message.trim() || selectedFile) && !uploading
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                : '#e5e7eb',
              borderRadius: '50%',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s'
            }}
          >
            {uploading ? (
              <Loader size={18} color="#9ca3af" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={18} color={(message.trim() || selectedFile) ? 'white' : '#9ca3af'} />
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      height: '100%',
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {showPatientPanel ? (
        <PatientPanel currentUser={currentUser} onBack={() => setShowPatientPanel(false)} />
      ) : selectedChat ? renderChatConversation() : renderChatList()}
      
      {/* Department Members Modal */}
      {showDeptMembersModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '360px',
            maxHeight: '70vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                  {selectedDeptName}
                </h3>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  {t('department.membersTitle')}
                </p>
              </div>
              <button onClick={() => setShowDeptMembersModal(false)} style={{ padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {loadingDeptMembers ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <RefreshCw size={24} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : deptMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  <User size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>{t('department.noMembers')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {deptMembers.map(member => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        gap: '12px'
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '12px'
                      }}>
                        {member.fullName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '14px', color: '#1f2937' }}>
                          {member.fullName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          @{member.username}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setShowDeptMembersModal(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '340px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{t('profile.title')}</h3>
              <button onClick={() => setShowProfileModal(false)} style={{ padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>
            
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              {/* Avatar */}
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  {myAvatar ? (
                    <img 
                      src={getFileUrl(myAvatar)} 
                      alt="Avatar" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ color: 'white', fontSize: '36px', fontWeight: '600' }}>
                      {currentUser?.fullName?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                
                {uploadingAvatar && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <RefreshCw size={24} color="white" style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                )}
              </div>
              
              {/* User Info */}
              <div style={{ textAlign: 'center' }}>
                <h4 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px' }}>
                  {currentUser?.fullName}
                </h4>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  {currentUser?.email || currentUser?.username}
                </p>
              </div>
              
              {/* Avatar Actions */}
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    opacity: uploadingAvatar ? 0.7 : 1
                  }}
                >
                  <Image size={16} />
                  {t('profile.uploadPhoto')}
                </button>
                
                {myAvatar && (
                  <button
                    onClick={handleAvatarDelete}
                    disabled={uploadingAvatar}
                    style={{
                      padding: '10px 16px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: uploadingAvatar ? 0.7 : 1
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
            
            <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setShowProfileModal(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('profile.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Toast Notification */}
      {patientToast && (
        <div
          onClick={() => {
            setPatientToast(null);
            if (!showPatientPanel) setShowPatientPanel(true);
          }}
          style={{
            position: 'absolute',
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 25px rgba(245, 158, 11, 0.4)',
            zIndex: 2000,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: '90%',
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          <style>{`
            @keyframes slideDown {
              from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <MessageSquare size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '600', fontSize: '13px' }}>
              {patientToast.type === 'queue' && `🔔 ${t('patient.toast.newPatientWaiting', 'New patient waiting in queue')}`}
              {patientToast.type === 'message' && `💬 ${patientToast.name}`}
              {patientToast.type === 'assigned' && `📋 ${t('patient.toast.sessionAssigned', 'Patient session assigned to you')}`}
            </div>
            {patientToast.type === 'message' && patientToast.content && (
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {patientToast.content.length > 50 ? patientToast.content.substring(0, 50) + '...' : patientToast.content}
              </div>
            )}
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '3px' }}>
              {t('patient.toast.clickToOpen', 'Click to open patient panel')}
            </div>
          </div>
          <X size={16} onClick={(e) => { e.stopPropagation(); setPatientToast(null); }} style={{ opacity: 0.7, flexShrink: 0 }} />
        </div>
      )}
    </div>
  );
};

export default ChatPanel;
